import React, { useState } from 'react';
import { Type, Image as ImageIcon, Link as LinkIcon, Plus, Minus, MoreHorizontal, Send, Clock, AlertCircle, AlertTriangle, Bug, ArrowRight } from 'lucide-react';
import FileUploadField from './FileUploadField';
import { api, ApiError } from '../../lib/api';

const inputClasses =
  'w-full px-4 py-2.5 rounded-lg bg-white/[0.03] border border-white/[0.06] text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-[#B0FC38]/30 focus:border-[#B0FC38]/30 focus:bg-white/[0.05] transition-all';
const labelClasses = 'block text-[11px] font-medium text-gray-400/80 mb-1.5 tracking-wide';

const CHANGE_TYPES = [
  { value: 'text', label: 'Text', desc: 'Update wording', icon: Type },
  { value: 'image', label: 'Image', desc: 'Swap or add an image', icon: ImageIcon },
  { value: 'link', label: 'Link', desc: 'Fix or change a link', icon: LinkIcon },
  { value: 'new_section', label: 'New section', desc: 'Add new content', icon: Plus },
  { value: 'remove', label: 'Remove', desc: 'Take content down', icon: Minus },
  { value: 'other', label: 'Other', desc: 'Something else', icon: MoreHorizontal },
];

// Before→after only makes sense for swapping wording or a link target.
const BEFORE_AFTER_TYPES = new Set(['text', 'link']);

const PRIORITIES = [
  { value: 'low', label: 'Low', desc: 'Whenever', icon: Clock, color: 'text-green-400', ring: 'border-green-400/40 bg-green-400/[0.06]' },
  { value: 'medium', label: 'Medium', desc: 'Normal', icon: AlertCircle, color: 'text-yellow-400', ring: 'border-yellow-400/40 bg-yellow-400/[0.06]' },
  { value: 'high', label: 'High', desc: 'Time-sensitive', icon: AlertTriangle, color: 'text-orange-400', ring: 'border-orange-400/40 bg-orange-400/[0.06]' },
  { value: 'critical', label: 'Critical', desc: 'Blocking', icon: Bug, color: 'text-red-400', ring: 'border-red-400/40 bg-red-400/[0.06]' },
];

const EMPTY = {
  title: '',
  pagePath: '',
  changeType: 'text',
  priority: 'medium',
  currentContent: '',
  desiredContent: '',
  description: '',
  attachments: [],
};

export default function EditRequestForm({ pages, onCreated }) {
  const [form, setForm] = useState({ ...EMPTY, pagePath: pages?.[0]?.path || '' });
  const [fieldError, setFieldError] = useState({});
  const [generalError, setGeneralError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const onChange = (field) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((prev) => ({ ...prev, [field]: value }));
    setFieldError((prev) => ({ ...prev, [field]: undefined }));
    setGeneralError('');
  };

  const showBeforeAfter = BEFORE_AFTER_TYPES.has(form.changeType);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setFieldError({});
    setGeneralError('');

    const payload = {
      title: form.title.trim(),
      pagePath: form.pagePath,
      changeType: form.changeType,
      priority: form.priority,
      description: form.description.trim(),
      currentContent: showBeforeAfter ? form.currentContent.trim() : '',
      desiredContent: showBeforeAfter ? form.desiredContent.trim() : '',
      attachments: form.attachments,
    };

    try {
      const data = await api.post('/api/edit-requests', payload);
      setForm({ ...EMPTY, pagePath: pages?.[0]?.path || '' });
      onCreated?.(data.editRequest);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.data?.field) setFieldError({ [err.data.field]: err.message });
        else setGeneralError(err.message || 'Failed to submit request');
      } else {
        setGeneralError('Network error, try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-5 p-6 md:p-7 rounded-2xl bg-white/[0.025] border border-white/[0.06]" noValidate>
      <div>
        <h3 className="text-sm font-medium text-white tracking-tight">New edit request</h3>
        <p className="text-[11px] text-gray-500 mt-0.5">
          Request a content change. The more detail you give, the faster NexSav can make it.
        </p>
      </div>

      {generalError && (
        <div className="px-3 py-2 rounded-lg bg-red-500/[0.08] border border-red-500/20 text-[13px] text-red-300/90">
          {generalError}
        </div>
      )}

      <div>
        <label className={labelClasses} htmlFor="edit-title">Title *</label>
        <input
          id="edit-title"
          type="text"
          value={form.title}
          onChange={onChange('title')}
          className={inputClasses}
          placeholder="Short summary, e.g. Update principal's welcome message"
          maxLength={200}
          required
        />
        {fieldError.title && <p className="mt-1 text-[11px] text-red-400">{fieldError.title}</p>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className={labelClasses} htmlFor="edit-page">Page *</label>
          <select id="edit-page" value={form.pagePath} onChange={onChange('pagePath')} className={inputClasses} required>
            {pages.map((p) => (
              <option key={p.id} value={p.path}>{p.title ? `${p.title} · ${p.path}` : p.path}</option>
            ))}
          </select>
          {fieldError.pagePath && <p className="mt-1 text-[11px] text-red-400">{fieldError.pagePath}</p>}
        </div>
        <div>
          <label className={labelClasses}>Priority *</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {PRIORITIES.map((p) => {
              const selected = form.priority === p.value;
              const Icon = p.icon;
              return (
                <button key={p.value} type="button" onClick={() => onChange('priority')(p.value)}
                  className={`text-left p-2.5 rounded-lg border transition-all ${selected ? p.ring : 'bg-white/[0.02] border-white/[0.06] hover:border-white/[0.12]'}`}>
                  <div className="flex items-center gap-1.5">
                    <Icon className={`w-3.5 h-3.5 ${p.color}`} />
                    <span className={`text-[12px] font-medium ${selected ? 'text-white' : 'text-gray-300'}`}>{p.label}</span>
                  </div>
                  <div className="text-[10px] text-gray-500 mt-0.5">{p.desc}</div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div>
        <label className={labelClasses}>Change type *</label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {CHANGE_TYPES.map((c) => {
            const selected = form.changeType === c.value;
            const Icon = c.icon;
            return (
              <button key={c.value} type="button" onClick={() => onChange('changeType')(c.value)}
                className={`text-left p-3 rounded-lg border transition-all ${selected ? 'bg-[#B0FC38]/[0.08] border-[#B0FC38]/40' : 'bg-white/[0.02] border-white/[0.06] hover:border-white/[0.12]'}`}>
                <div className="flex items-center gap-2">
                  <Icon className={`w-3.5 h-3.5 ${selected ? 'text-[#B0FC38]' : 'text-gray-500'}`} />
                  <span className={`text-[13px] font-medium ${selected ? 'text-white' : 'text-gray-300'}`}>{c.label}</span>
                </div>
                <div className="text-[11px] text-gray-500 mt-0.5">{c.desc}</div>
              </button>
            );
          })}
        </div>
      </div>

      {showBeforeAfter && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 p-4 rounded-xl bg-white/[0.015] border border-white/[0.05]">
          <div>
            <label className={labelClasses} htmlFor="edit-current">{form.changeType === 'link' ? 'Current link' : 'Current text'}</label>
            <textarea id="edit-current" value={form.currentContent} onChange={onChange('currentContent')} rows={3}
              className={`${inputClasses} resize-y`} placeholder={form.changeType === 'link' ? 'The link as it is now' : 'Copy the text exactly as it appears now'} />
          </div>
          <div className="relative">
            <ArrowRight className="hidden md:block w-4 h-4 text-gray-600 absolute -left-3.5 top-9" />
            <label className={labelClasses} htmlFor="edit-desired">{form.changeType === 'link' ? 'New link' : 'What it should say'}</label>
            <textarea id="edit-desired" value={form.desiredContent} onChange={onChange('desiredContent')} rows={3}
              className={`${inputClasses} resize-y`} placeholder={form.changeType === 'link' ? 'Where it should point instead' : 'The exact new wording'} />
          </div>
        </div>
      )}

      <div>
        <label className={labelClasses} htmlFor="edit-description">Description *</label>
        <textarea
          id="edit-description"
          value={form.description}
          onChange={onChange('description')}
          rows={4}
          className={`${inputClasses} resize-y min-h-[100px]`}
          placeholder="Any extra context: where on the page, why, anything the team should know."
          required
        />
        {fieldError.description && <p className="mt-1 text-[11px] text-red-400">{fieldError.description}</p>}
      </div>

      <div>
        <label className={labelClasses}>Reference files</label>
        <FileUploadField
          attachments={form.attachments}
          onChange={(next) => onChange('attachments')(next)}
          disabled={submitting}
          scope="edit-requests"
        />
      </div>

      <div className="flex items-center justify-end pt-2">
        <button type="submit" disabled={submitting}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#B0FC38] text-gray-900 text-sm font-semibold hover:bg-[#9BE22E] shadow-lg shadow-[#B0FC38]/20 hover:shadow-[#B0FC38]/30 transition-all disabled:opacity-60 disabled:cursor-not-allowed">
          {submitting ? (
            <><span className="w-3.5 h-3.5 border-2 border-gray-900 border-t-transparent rounded-full animate-spin" /> Submitting…</>
          ) : (
            <><Send className="w-4 h-4" /> Submit request</>
          )}
        </button>
      </div>
    </form>
  );
}
